import React from 'react';
import { Check, Coffee, Heart, Truck } from 'lucide-react';
import ShareButton from '../ShareButton';

interface OrderSuccessProps {
  purchaseType: 'monthly' | 'single';
  roastType: string;
  customerName: string;
  isMarblehead?: boolean;
  selectedTier?: any;
  onClose: () => void;
}

export default function OrderSuccess({ purchaseType, roastType, customerName, isMarblehead = false, selectedTier, onClose }: OrderSuccessProps) {

  const roastEmojis = {
    light: '🕊️',
    medium: '🌿',
    dark: '🌋',
    espresso: '🚀',
    'barnegat-breakfast': '🌅',
    'soleless-shakedown': '⚡',
    'nunnepog-fog': '🌫️'
  };

  return (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      {/* Success Animation */}
      <div className="relative">
        <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
          <Check className="w-12 h-12 text-white" />
        </div>
        <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-full animate-ping" />
      </div>

      {/* Main Message */}
      <div className="space-y-4">
        <div className="text-6xl mb-4">
          {roastEmojis[roastType as keyof typeof roastEmojis]}
        </div>
        <h2 className="text-4xl font-bold text-white leading-tight">
          {purchaseType === 'monthly' 
            ? `Welcome to Bond, ${customerName}! 🎉`
            : `Order Confirmed, ${customerName}! ☕`
          }
        </h2>
        <p className="text-xl text-white/80 leading-relaxed max-w-lg mx-auto">
          {purchaseType === 'monthly' 
            ? "Your monthly coffee delivery is locked and loaded. We're basically your new caffeine dealers now."
            : "Your coffee order is confirmed and ready to rock. Consider us your new favorite coffee connection."
          }
        </p>
      </div>

      {/* What Happens Next */}
      <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-8 rounded-2xl border border-[#fda36b]/20">
        <h3 className="text-2xl font-bold text-[#fda36b] mb-6">Get Ready for Coffee Magic! ☕</h3>
        
        <div className="space-y-6 text-left">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#fda36b]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <Coffee className="w-4 h-4 text-[#fda36b]" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">Your Coffee Journey Begins Now</h4>
              <p className="text-white/80">
                Your order is confirmed and we're already getting excited to roast your perfect {roastType} beans! 
                Time to start clearing space in your coffee cabinet and maybe practice your "mmm, this is amazing" face.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#fda36b]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <Heart className="w-4 h-4 text-[#fda36b]" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">We're Roasting Just for You</h4>
              <p className="text-white/80">
                Your {roastType} beans will be roasted to order within the next few days. We're talking 
                "still-warm-from-the-roaster" fresh. No sitting on shelves, no compromises – just peak flavor 
                heading straight to your cup.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-[#fda36b]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
              <Truck className="w-4 h-4 text-[#fda36b]" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-2">Delivery Day is Coming!</h4>
              <p className="text-white/80">
                {isMarblehead 
                  ? "Get ready for the doorbell! Adam and Chris will personally deliver your coffee (yes, probably in the Japanese mini truck). It's like having your own personal coffee fairy godparents."
                  : "Your carefully packaged beans will ship out within 2-3 business days. Track your package and start the countdown to coffee bliss!"
                }
              </p>
            </div>
          </div>

          {purchaseType === 'monthly' && (
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-[#fda36b]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-[#fda36b] text-sm font-bold">∞</span>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Monthly Coffee Happiness Incoming</h4>
                <p className="text-white/80">
                  Mark your calendar! Your monthly coffee delivery will become the highlight of your month. 
                  Fresh roasted beans arriving right when you need them most. You can pause, modify, or cancel 
                  anytime – but honestly, why would you want to?
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fun Footer */}
      <div className="bg-white/5 p-6 rounded-lg border border-white/10">
        <p className="text-white/80 text-lg">
          <span className="font-semibold text-[#fda36b]">Pro tip while you wait:</span> Start planning your first cup! 
          Maybe clean that coffee maker, practice your pour-over technique, or just sit there grinning because 
          amazing coffee is heading your way. We won't judge any of these choices.
        </p>
      </div>

      {/* Close Button */}
      <button
        onClick={onClose}
        className="bg-[#fda36b] text-[#132947] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#fda36b]/90 transition-all duration-300"
      >
        Perfect! Close This
      </button>

      {/* Share Feature */}
      <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-6 rounded-lg border border-[#fda36b]/20">
        <div className="text-center space-y-4">
          <h3 className="text-xl font-bold text-[#fda36b]">
            {isMarblehead ? 'Help Build Our Marblehead Community!' : 'Love Bond Coffee?'}
          </h3>
          <p className="text-white/90">
            {isMarblehead 
              ? 'Want to help your Marblehead neighbors get better coffee prices? The more people who join, the cheaper it gets for everyone!'
              : 'Help us grow by sharing Bond with your friends and family! The more people who discover great coffee, the better.'
            }
          </p>
          <div className="pt-2">
            <ShareButton variant="secondary" size="large" />
          </div>
        </div>
      </div>
    </div>
  );
}